import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * A panel.
 *
 * Elevation is a named idea rather than a shadow class, so raising something
 * changes one word and stays consistent with everything else at that level.
 */

export type SurfaceMaterial = 'solid' | 'glass' | 'glass-strong' | 'sunken';
export type SurfaceElevation = 0 | 1 | 2 | 3;

const MATERIAL_CLASSES: Record<SurfaceMaterial, string> = {
  solid: 'bg-surface-1 border border-edge',
  glass: 'glass',
  'glass-strong': 'glass-strong',
  sunken: 'bg-bg-sunken border border-edge',
};

const ELEVATION_CLASSES: Record<SurfaceElevation, string> = {
  0: '',
  1: 'shadow-1',
  2: 'shadow-2',
  3: 'shadow-3',
};

/**
 * Written out in full rather than built by interpolation.
 *
 * Tailwind finds classes by scanning source text for literal strings. A
 * template literal such as `rounded-[var(--radius-${radius})]` matches nothing
 * during that scan, so the utility is never generated and the corner silently
 * stays square. Nothing fails at build time, which is what makes it worth
 * spelling out.
 */
const RADIUS_CLASSES = {
  sm: 'rounded-[var(--radius-sm)]',
  md: 'rounded-[var(--radius-md)]',
  lg: 'rounded-[var(--radius-lg)]',
  xl: 'rounded-[var(--radius-xl)]',
} as const;

export type SurfaceRadius = keyof typeof RADIUS_CLASSES;

export interface SurfaceProps extends HTMLAttributes<HTMLDivElement> {
  readonly material?: SurfaceMaterial;
  readonly elevation?: SurfaceElevation;
  readonly radius?: SurfaceRadius;
}

export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { material = 'solid', elevation = 0, radius = 'lg', className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        MATERIAL_CLASSES[material],
        // Glass carries its own shadow, so adding another would double it.
        material === 'solid' || material === 'sunken' ? ELEVATION_CLASSES[elevation] : '',
        RADIUS_CLASSES[radius],
        className,
      )}
      {...rest}
    />
  );
});
